import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { HttpService } from "../../service/HttpService";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import { BsArrowLeft } from "react-icons/bs";
import dayjs from "dayjs";

const InventoryGraph = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const departmentId = location.state?.departmentId || "";
  const departmentName = location.state?.departmentName || "";
  const [loading, setLoading] = useState(false);
  const [graphData, setGraphData] = useState([]);

  useEffect(() => {
    if (!departmentId) return;
    setLoading(true);
    const httpService = new HttpService();
    httpService
      .get(`/inventory/graph-data?department_id=${departmentId}`)
      .then((response) => {
        // Transform data so all bars have height 1, but color is based on exist value
        const transformedData = (response?.data || []).map((item) => ({
          ...item,
          barHeight: 1, // All bars will be height 1
          exist: item.exist, // Keep original exist value for color logic
        }));
        setGraphData(transformedData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [departmentId]);

  // Color logic: exist=0 => light green, exist=1 => dark green
  const getBarColor = (exist) => (exist === 0 ? "#C1DFD5" : "#00A272");

  // Custom tooltip to show only name
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const onHandQty = parseFloat(data.on_hand_qty);
      const reorderThreshold = parseFloat(data.reorder_threshold);
      const stockDate = data.stock_date;
      const exist = data.exist;
      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          <p className={exist === 0 ? `m-0` : ""}>
            <strong>{label}</strong>
          </p>
          {exist > 0 && (
            <>
              <p>
                <strong>On Hand Quantity:</strong>{" "}
                {isNaN(onHandQty) ? "--" : onHandQty}
              </p>
              <p>
                <strong>Reorder Threshold:</strong>{" "}
                {isNaN(reorderThreshold) ? "as needed" : reorderThreshold}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {stockDate ? dayjs(stockDate).format("MM/DD/YYYY") : "--"}
              </p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  const renderMultilineTick = (props) => {
    const { x, y, payload } = props;
    const name = payload.value;
    const words = name.split(" ");
    const lines = [];

    for (let i = 0; i < words.length; i += 3) {
      lines.push(words.slice(i, i + 3).join(" "));
    }

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          textAnchor="end"
          fill="#666"
          fontSize={11}
          transform="rotate(-45)"
        >
          {lines.map((line, idx) => (
            <tspan x={0} dy={idx === 0 ? 0 : 14} key={idx}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  return (
    <div className="inventory-graph-page" style={{ padding: 24 }}>
      {loading && <FullScreenLoader />}
      {/* Header with back button and department name */}
      {/* <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none',
            border: 'none',
            color: '#00A272',
            fontSize: 24,
            cursor: 'pointer',
            marginRight: 16,
            fontWeight: 700
          }}
        >
          &#8592;
        </button>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: 24 }}>
          {departmentName ? `${departmentName.replace(/_/g, ' ')} - ` : ''}Inventory Graph
        </h2>
      </div> */}
      <div className="page-title">
        <div className="title-wrap">
          <h1>
            <span className="back">
              <BsArrowLeft onClick={() => navigate(-1)} />
            </span>
            {departmentName ? `${departmentName.replace(/_/g, " ")} - ` : ""}
            Inventory Monitor
          </h1>
        </div>
      </div>
      <div style={{ width: "100%", overflowX: "auto" }}>
        <div
          style={{
            minWidth: Math.max(600, graphData.length * 60),
            height: 500,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={graphData}
              width={graphData.length * 60}
              height={500}
              margin={{ top: 20, right: 30, left: 70, bottom: 80 }}
            >
              <CartesianGrid strokeDasharray="2 2" />
              <XAxis
                dataKey="name"
                interval={0}
                angle={-30}
                textAnchor="end"
                height={80}
                tick={renderMultilineTick}
              />
              <YAxis hide={true} />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="barHeight"
                name="Exist"
                barSize={50}
                isAnimationActive={false}
                fill="#00a272"
                {...{
                  shape: (props) => {
                    const { x, y, width, height, payload } = props;
                    return (
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        fill={getBarColor(payload.exist)}
                        rx={8}
                      />
                    );
                  },
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default InventoryGraph;

{
  /* <ResponsiveContainer width="100%" height={400}>
        <BarChart data={graphData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="2 2" />
          <XAxis dataKey="name" />
          <YAxis allowDecimals={false} domain={[0, 1]} />
          <Tooltip />
          <Bar
            dataKey="exist"
            name="Exist"
            barSize={90}
            isAnimationActive={false}
            label={{ position: "top" }}
            fill="#00a272"
            // Use a function to set color per bar
            {
              ...{
                shape: (props) => {
                  const { x, y, width, height, payload } = props;
                  return (
                    <rect
                      x={x}
                      y={y}
                      width={width}
                      height={height}
                      fill={getBarColor(payload.exist)}
                      rx={8}
                    />
                  );
                },
              }
            }
          />
        </BarChart>
      </ResponsiveContainer> */
}
